/** @jsx jsx */
import { useRef, useContext } from 'react';
import { useDay } from './useDatePicker';
import { jsx, useTheme } from '@front10/base-elements';
import { DatePickerContext } from './datePickerContext';
import { addDays } from 'date-fns';
import Box from '../../elements/Box';
import { BlackoutDates } from './../../containers/VerticalBookingContainer/blackoutDates';

type GetColorProps = {
  selectedFirstOrLastColor: string;
  normalColor: string;
  selectedColor: string;
  rangeHoverColor: string;
  disabledColor: string;
};

function getColor(
  isSelected: boolean,
  isSelectedStartOrEnd: boolean,
  isWithinHoverRange: boolean,
  isDisabled: boolean
) {
  return ({
    selectedFirstOrLastColor,
    normalColor,
    selectedColor,
    rangeHoverColor,
    disabledColor
  }: GetColorProps) => {
    if (isSelectedStartOrEnd) {
      return selectedFirstOrLastColor;
    } else if (isSelected) {
      return selectedColor;
    } else if (isWithinHoverRange) {
      return rangeHoverColor;
    } else if (isDisabled) {
      return disabledColor;
    } else {
      return normalColor;
    }
  };
}

type DayProps = {
  dayLabel?: string;
  date: Date;
  blackoutDatesIntegration?: boolean;
  fetchBlackoutDates?: BlackoutDates | null;
  focusedInput?: string | null;
};

export function Day({ dayLabel, date, ...rest }: DayProps) {
  const dayRef = useRef(null);
  const {
    theme: { text, layout }
  } = useTheme();
  const {
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
    isStartDate,
    isEndDate
  } = useContext(DatePickerContext);
  const {
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    disabledDate,
    onClick,
    onKeyDown,
    onMouseEnter,
    tabIndex,
    isSelectedStartOrEndAndBlackout,
    isAMiddleAndBlackout
  } = useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    dayRef,
    blackoutDatesIntegration: rest?.blackoutDatesIntegration,
    fetchBlackoutDates: rest?.fetchBlackoutDates,
    focusedInput: rest?.focusedInput
  });
  if (!dayLabel) {
    return <div />;
  }
  const getColorFn = getColor(isSelected, isSelectedStartOrEnd, isWithinHoverRange, disabledDate);

  const styles = layout?.datepickerStyles as any;

  const dayVariant = function getVariant() {
    if (layout && text) {
      if (isAMiddleAndBlackout) {
        return styles?.dayMiddleAndBlackoutDay ?? text.fontSize5;
      } else if (isSelectedStartOrEndAndBlackout) {
        return styles?.dayStartOrEndAnaBlackoutDay ?? text.fontSize5;
      } else if (isSelectedStartOrEnd) {
        return styles?.dayStartOrEnd ?? text.fontSize5;
      } else if (isSelected) {
        return styles?.daySelected ?? text.fontSize5;
      } else if (isWithinHoverRange) {
        return styles?.dayWithinRange ?? text.fontSize5;
      } else if (disabledDate) {
        return styles?.dayDisabled ?? text.fontSize5;
      } else {
        return styles?.dayNormal ?? text.fontSize5;
      }
    }
  };

  const bg = getColorFn({
    selectedFirstOrLastColor: 'primary',
    normalColor: '#fff',
    selectedColor: 'primary',
    rangeHoverColor: 'primary',
    disabledColor: '#fff'
  });

  const tomorrow = addDays(date, 1);

  const styleStartOrEnd = () => {
    if (layout) {
      if (isStartDate(date) && !isEndDate(date)) {
        return styles?.dayStart;
      } else if (isEndDate(date) && !isStartDate(date)) {
        return styles?.dayEnd;
      } else {
        return '';
      }
    }
    return '';
  };

  return (
    <Box style={styleStartOrEnd()}>
      <button
        onClick={onClick}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        tabIndex={tabIndex}
        type="button"
        ref={dayRef}
        sx={{
          padding: '2',
          margin: 0,
          border: '0',
          width: '100%',
          minWidth: '38px',
          variant: 'text.fontSize5',
          outline: 'none',
          color: getColorFn({
            selectedFirstOrLastColor: '#fff',
            normalColor: 'text',
            selectedColor: '#fff',
            rangeHoverColor: '#fff',
            disabledColor: 'disabledDayColor'
          }),
          bg,
          ...(!isSelected &&
            !disabledDate &&
            !isSelectedStartOrEnd &&
            !isWithinHoverRange && {
              '&:hover': {
                borderRadius: 'default',
                position: 'relative',
                bg: 'white',
                boxShadow: 'inset 0 0 0 1px',
                variant: 'layout.datepickerStyles.buttonsHover'
              }
            }),
          ...dayVariant(),
          ...(isStartDate(date) && {
            borderTopLeftRadius: 'default',
            borderBottomLeftRadius: 'default'
          }),
          ...(!isDateSelected(tomorrow) &&
            !isDateHovered(tomorrow) && {
              borderTopRightRadius: 'default',
              borderBottomRightRadius: 'default'
            })
        }}
      >
        {dayLabel}
      </button>
    </Box>
  );
}
